import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { listComprasDisponibles, vincularCompras, type CompraDisponible } from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { formatMoney } from '@/shared/lib/money';
import { fechaImp } from './componentes';

type Codigo = 'ARS' | 'USD' | 'MXN';

/**
 * Vincular compras que ya existen (cargadas antes de crear la importación).
 * Lista las que no tienen importación, primero las del fabricante. Vincular
 * sólo anota el contenedor: no cambia montos, pagos ni stock.
 */
export function VincularComprasDialog({
  importacionId,
  numero,
  onClose,
  onDone,
}: {
  importacionId: number;
  numero: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const q = useQuery({
    queryKey: ['importaciones', 'compras-disponibles', importacionId],
    queryFn: () => listComprasDisponibles(importacionId),
    staleTime: 0,
  });

  const filtradas = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    const items = q.data ?? [];
    if (!t) return items;
    return items.filter((c) =>
      [c.id, c.proveedor, c.comprobante, c.numero, c.fecha ? fechaImp(c.fecha) : ''].some((v) => v.toLowerCase().includes(t)),
    );
  }, [q.data, busqueda]);

  const vincular = useMutation({
    mutationFn: () => vincularCompras(importacionId, [...elegidas]),
    onSuccess: (r) => {
      if (r.vinculadas > 0) {
        toast.success(r.vinculadas === 1 ? 'Compra vinculada.' : `${r.vinculadas} compras vinculadas.`);
      }
      for (const e of r.errores) toast.error(e.message);
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudieron vincular las compras.'),
  });

  const alternar = (id: string) =>
    setElegidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && !vincular.isPending && onClose()}>
      <DialogContent className="max-w-3xl" processing={vincular.isPending && 'Vinculando…'}>
        <DialogHeader>
          <DialogTitle>Vincular compras existentes</DialogTitle>
          <DialogDescription>
            Compras sin importación, primero las del fabricante. Quedan asociadas a la importación N° {numero}; no
            cambian sus montos, pagos ni stock.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              variant="filled"
              className="pl-9"
              placeholder="Buscar por N° de compra, proveedor, comprobante o fecha"
              aria-label="Buscar compra"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoFocus
            />
          </div>
          {q.isPending ? (
            <Skeleton className="h-48 w-full rounded-xl" />
          ) : q.isError ? (
            <p className="text-sm text-destructive">No se pudieron cargar las compras.</p>
          ) : filtradas.length === 0 ? (
            <p className="rounded-lg border border-dashed bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
              {busqueda ? `No hay compras para "${busqueda}".` : 'No hay compras sin importación.'}
            </p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto rounded-xl border">
              <ul className="divide-y divide-border">
                {filtradas.map((c) => (
                  <FilaCompra key={c.id} c={c} elegida={elegidas.has(c.id)} onToggle={() => alternar(c.id)} />
                ))}
              </ul>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={vincular.isPending}>
            Cancelar
          </Button>
          <Button disabled={elegidas.size === 0 || vincular.isPending} onClick={() => vincular.mutate()}>
            {elegidas.size > 1 ? `Vincular ${elegidas.size} compras` : 'Vincular compra'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilaCompra({ c, elegida, onToggle }: { c: CompraDisponible; elegida: boolean; onToggle: () => void }) {
  return (
    <li>
      <label className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm hover:bg-secondary/60">
        <Checkbox checked={elegida} onCheckedChange={onToggle} aria-label={`Elegir ${c.proveedor} ${c.numero}`} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate font-medium text-foreground">
            <span className="truncate">{c.proveedor}</span>
            {c.delFabricante && (
              <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-sky-700">
                Fabricante
              </span>
            )}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            Compra {c.id} · {[c.comprobante, c.numero].filter(Boolean).join(' ') || '—'} · {fechaImp(c.fecha)} · {c.status}
          </p>
        </div>
        <span className="shrink-0 tabular-nums font-semibold">{formatMoney(c.total, c.moneda as Codigo)}</span>
      </label>
    </li>
  );
}
